import type { CityServiceAreaContent } from "./cityServiceAreas";

/**
 * Additional city guides selected to complete the five most-populated incorporated
 * places in each owner-approved full-service state, using Census Vintage 2025.
 * Local references are planning context only; they do not establish a project,
 * office, inventory position, automatic work acceptance, or customer relationship.
 */
export const topFiveCityServiceAreaAdditions = [
  {
    stateSlug: "new-york",
    citySlug: "rochester",
    city: "Rochester",
    stateName: "New York",
    metaTitle: "Rochester Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Rochester projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Rochester buyers may encounter restroom-partition needs in downtown and neighborhood commercial/mixed-use properties, civic and public facilities, arts and cultural venues, transportation facilities, and university or medical campuses. The City’s comprehensive plan addresses housing, transportation, economic growth, historic preservation, neighborhoods, and downtown, while the University of Rochester describes extensive building, medical-center, campus, and renovation operations; these public sources do not establish a Partition Stalls project, customer relationship, local office, or completed work, so any opportunity remains subject to project-specific review.",
    facilityTypes: [
      "Civic and public-service buildings",
      "Downtown and neighborhood commercial or mixed-use properties",
      "University, medical, and student-housing facilities",
      "Arts, cultural, and visitor venues",
      "Transportation facilities and terminals",
      "Multifamily or residential amenity buildings"
    ],
    planningInputs: [
      "Facility use and occupancy, including public, staff, student, patient, or visitor traffic",
      "Restroom count, partition layout, stall dimensions, and accessibility requirements",
      "New construction, renovation, replacement, or phased-occupancy scope",
      "Existing panel, hardware, substrate, plumbing, and site-condition information",
      "Finish, material, privacy, durability, and cleaning requirements",
      "Site access, delivery constraints, work hours, permits, and coordination with other trades"
    ],
    nearbyAreas: [
      "Downtown Rochester",
      "East End",
      "Neighborhood of the Arts",
      "South Wedge",
      "Highland Park",
      "Brighton",
      "Irondequoit"
    ],
    references: [
      {
        context: "The City’s adopted 15-year plan covers housing, transportation, economic growth, historic preservation, strong neighborhoods, placemaking, and downtown-oriented community development.",
        label: "City of Rochester — Rochester 2034 Comprehensive Plan",
        url: "https://www.cityofrochester.gov/departments/city-planning/rochester-2034"
      },
      {
        context: "The university reports infrastructure, maintenance, engineering, construction and renovation services across the River Campus, Medical Center, Eastman School of Music, Memorial Art Gallery, and other regional sites.",
        label: "University of Rochester — Facilities Operations",
        url: "https://adminfinance.rochester.edu/departments/facilities-operations/"
      }
    ]
  },
  {
    stateSlug: "new-york",
    citySlug: "yonkers",
    city: "Yonkers",
    stateName: "New York",
    metaTitle: "Yonkers Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Yonkers projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Yonkers buyers may encounter restroom-partition needs across downtown waterfront and commercial-corridor projects, civic and library facilities, housing and mixed-use buildings, parks and recreation destinations, and transportation-related or regional business facilities. The City’s planning materials identify waterfront access, a marina, dock-and-dine, streetscape work, housing, retail, public facilities, and transportation as relevant local contexts, while the county airport source documents a major regional business-aviation facility; these sources do not establish a Partition Stalls project or customer relationship, so any opportunity remains subject to project-specific review.",
    facilityTypes: [
      "Civic and municipal buildings",
      "Libraries and cultural or public-access facilities",
      "Multifamily housing and mixed-use developments",
      "Retail and restaurant properties along commercial corridors",
      "Parks, waterfront, marina, and recreation facilities",
      "Transportation and regional business facilities"
    ],
    planningInputs: [
      "Project address, facility type, and service-area or floor plan details",
      "Partition layout, panel dimensions, compartment count, and door or hardware requirements",
      "Mounting conditions, substrate, ceiling height, plumbing clearances, and accessibility requirements",
      "Material, finish, color, privacy, and code or owner standards",
      "Site access, occupied-building constraints, delivery path, installation hours, and coordination with other trades",
      "Bid scope, drawings or specifications, approval requirements, and requested schedule for project-specific review"
    ],
    nearbyAreas: [
      "Downtown Waterfront",
      "Getty Square",
      "South Broadway",
      "Ashburton Avenue corridor",
      "Alexander Street waterfront",
      "Central Park Avenue corridor",
      "Nodine Hill"
    ],
    references: [
      {
        context: "Official city planning page identifying the downtown waterfront DRI boundary and potential projects including a pedestrian bridge, esplanade, marina, Hudson River beach, dock-and-dine facility, housing, and Ashburton, Warburton, and Point Street improvements.",
        label: "City of Yonkers — Downtown Revitalization Initiative (DRI)",
        url: "https://www.yonkersny.gov/1137/Downtown-Revitalization-Initiative-DRI"
      },
      {
        context: "Official city planning archive describing community-facility, transportation, housing, waterfront, commercial-corridor, Alexander Street, Ashburton Avenue, Ravine Avenue, and neighborhood planning contexts.",
        label: "City of Yonkers — Historic Planning Documents",
        url: "https://www.yonkersny.gov/1241/Historic-Planning-Documents"
      },
      {
        context: "Official county airport page describing the airport as an economic-development and business-aviation facility with a main terminal, commercial service, general aviation, and related planning and environmental functions.",
        label: "Westchester County — About the Westchester County Airport",
        url: "https://airport.westchestercountyny.gov/about-us"
      }
    ]
  },
  {
    stateSlug: "new-york",
    citySlug: "syracuse",
    city: "Syracuse",
    stateName: "New York",
    metaTitle: "Syracuse Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Syracuse projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Syracuse buyers may encounter restroom-partition needs in municipal and commercial redevelopment settings, airport terminal and passenger-support areas, and university residence, dining, student-center, wellness, and athletic facilities. These public sources describe local facility contexts only; they do not establish a Partition Stalls project, customer relationship, local office or inventory, automatic job acceptance, product guarantee, or fixed lead time, so any opportunity remains subject to project-specific review.",
    facilityTypes: [
      "Municipal and civic facilities",
      "Commercial and mixed-use redevelopment",
      "Airport terminals and passenger-support facilities",
      "University residence halls and academic buildings",
      "Student centers, dining, wellness, and athletic facilities",
      "Parks and recreation facilities"
    ],
    planningInputs: [
      "Facility type, address, occupancy, and new-build versus renovation scope",
      "Partition count, room layouts, panel dimensions, and door swings",
      "Mounting conditions, substrate, ceiling height, and plumbing/MEP clearances",
      "Accessibility and applicable code requirements, including restroom turning clearances",
      "Material, color, hardware, privacy, and vandal-resistance preferences",
      "Phasing, site access, shutdowns, coordination, and requested installation window"
    ],
    nearbyAreas: [
      "Downtown Syracuse",
      "Armory Square",
      "University Hill",
      "Near Westside",
      "Eastwood",
      "East Syracuse",
      "Town of DeWitt"
    ],
    references: [
      {
        context: "The city’s official site identifies business assistance, commercial-development planning, permits and inspections, public works, parks, and neighborhood/community services relevant to local facility planning.",
        label: "City of Syracuse — Official Home and Business/Real Estate Development Resources",
        url: "https://www.syr.gov/"
      },
      {
        context: "The airport authority’s public page lists terminal maps, parking, shop-and-dine services, security, accessibility, and airport-authority information, establishing an airport facility context.",
        label: "Syracuse Hancock International Airport — Official Airport Home",
        url: "https://flysyracuse.com/"
      },
      {
        context: "The university describes 21 residence halls, student centers, more than 25 campus dining locations, and a wellness hub, establishing a substantial institutional facility context.",
        label: "Syracuse University — Campus Life",
        url: "https://www.syracuse.edu/campus-life/"
      }
    ]
  },
  {
    stateSlug: "new-jersey",
    citySlug: "paterson",
    city: "Paterson",
    stateName: "New Jersey",
    metaTitle: "Paterson Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Paterson projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Paterson buyers may encounter restroom-partition needs in municipal and civic buildings, downtown and commercial corridors, historic-preservation and visitor settings, public-facility improvement projects, schools or other education facilities, healthcare-related sites, and transit facilities. The City of Paterson, Passaic County, and NJ TRANSIT sources describe planning, construction, public-space, economic-development, healthcare-access, and station contexts; they do not establish a Partition Stalls project, customer relationship, completed work, local office or inventory, automatic job acceptance, product guarantee, or fixed lead time, so any opportunity requires project-specific review.",
    facilityTypes: [
      "Municipal and civic buildings",
      "Downtown and commercial properties",
      "Historic, cultural, and visitor facilities",
      "Schools and education facilities",
      "Healthcare facilities",
      "Transit stations and related public facilities"
    ],
    planningInputs: [
      "Facility address, occupancy, and restroom locations",
      "Fixture count and partition-panel layout",
      "Panel dimensions, door swings, privacy clearances, and accessibility requirements",
      "Material, finish, hardware, and color preferences",
      "Existing-condition measurements, demolition needs, and substrate or mounting conditions",
      "Site access, phasing, operating constraints, and requested installation window for project-specific review"
    ],
    nearbyAreas: [
      "Downtown Paterson",
      "Great Falls Historic District",
      "Dublin neighborhood",
      "Eastside Park Historic District",
      "Clifton",
      "Haledon",
      "Prospect Park"
    ],
    references: [
      {
        context: "Official city page describing planning and zoning, historic preservation, community improvements, UCC/construction, public spaces, downtown/commercial-corridor revitalization, and public-facility improvement applications.",
        label: "City of Paterson Department of Economic Development",
        url: "https://www.patersonnj.gov/department/index.php?structureid=118"
      },
      {
        context: "Official station page documenting the Paterson rail station, parking, accessibility, bike facilities, ticketing, and its Market and Ward Streets setting.",
        label: "NJ TRANSIT Paterson Station",
        url: "https://www.njtransit.com/station/paterson-station"
      },
      {
        context: "Official county study describing regional connections involving Paterson, Clifton, Newark, employment, education, healthcare, commercial development, and Great Falls National Historical Park.",
        label: "Passaic County Paterson-Newark Transit Market Study",
        url: "https://www.passaiccountynj.org/departments/planning-economic-development/plans-and-technical-studies/paterson-newark-transit-market-study"
      }
    ]
  },
  {
    stateSlug: "new-jersey",
    citySlug: "elizabeth",
    city: "Elizabeth",
    stateName: "New Jersey",
    metaTitle: "Elizabeth Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Elizabeth projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Elizabeth’s official economic-development information describes a mix of downtown commercial and mixed-use activity, Elizabethport neighborhood redevelopment, business districts, neighborhoods, parks, and recreation facilities, while identifying airport, seaport, roadway, and rail connectivity. NJ TRANSIT’s Elizabeth Station page documents a commuter-rail facility with parking, ticketing, bike storage, and Northeast Corridor and North Jersey Coast service; regional transportation infrastructure may also include airport- and port-related sites. These public sources provide planning context only and do not establish a Partition Stalls project, customer relationship, completed work, local office or inventory, or automatic ability to accept a job; any opportunity requires project-specific review.",
    facilityTypes: [
      "Municipal and public-service buildings",
      "Downtown commercial and mixed-use properties",
      "Retail and shopping developments",
      "Airport-related facilities",
      "Port, warehouse, and logistics facilities",
      "Commuter-rail and transit facilities"
    ],
    planningInputs: [
      "Facility address, neighborhood or district, and site access constraints",
      "New construction, renovation, or replacement scope",
      "Panel material, finish, hardware, and privacy requirements",
      "Number of compartments, urinals, and accessible layouts",
      "Field dimensions, restroom plans, and mounting conditions",
      "Requested installation window, site rules, and coordination requirements"
    ],
    nearbyAreas: [
      "Elizabethport",
      "Downtown Elizabeth",
      "Midtown",
      "North Elizabeth",
      "Bayway",
      "Newark",
      "Linden"
    ],
    references: [
      {
        context: "The city page identifies Elizabethport redevelopment, downtown commercial and mixed-use initiatives, business-district and neighborhood revitalization, recreation facilities, and the city’s airport, seaport, roadway, and rail connectivity.",
        label: "City of Elizabeth — Elizabeth Development Company",
        url: "https://www.elizabethnj.org/268/Elizabeth-Development-Company"
      },
      {
        context: "The official station page documents Elizabeth Station’s parking, accessibility, bike facilities, ticketing services, and Northeast Corridor and North Jersey Coast commuter-rail lines.",
        label: "NJ TRANSIT — Elizabeth Station",
        url: "https://www.njtransit.com/station/elizabeth-station"
      },
      {
        context: "The regional authority describes infrastructure serving air, land, rail, and sea transportation and links Newark Liberty Airport and other regional transportation facilities.",
        label: "Port Authority of New York and New Jersey — Keeping the Region Moving",
        url: "https://www.panynj.gov/port-authority/en/index.html"
      }
    ]
  },
  {
    stateSlug: "pennsylvania",
    citySlug: "reading",
    city: "Reading",
    stateName: "Pennsylvania",
    metaTitle: "Reading Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Reading projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Reading buyers may encounter restroom-partition needs in downtown and neighborhood redevelopment, public and civic buildings, cultural or entertainment venues, food-service properties, and aviation-related facilities. The City of Reading describes planning, zoning, building/trade permitting, property-maintenance inspections, and neighborhood/redevelopment planning, while Reading Regional Airport identifies general-aviation, business, charter, training, museum, and public-safety uses; any scope, code, access, and finish requirements warrant project-specific review. These public sources do not establish a Partition Stalls project, local office or inventory, completed project, customer relationship, or automatic ability to accept work.",
    facilityTypes: [
      "Municipal and civic buildings",
      "Downtown or neighborhood redevelopment properties",
      "Restaurants, grocery, and other food-service facilities",
      "Cultural, museum, sports, and entertainment venues",
      "General-aviation, aviation-business, and airport-support facilities"
    ],
    planningInputs: [
      "Facility type, address, and occupancy/use status",
      "Partition layout, number of compartments, panel dimensions, and mounting conditions",
      "Required material, finish, hardware, privacy, and accessibility details",
      "Existing-condition photos or drawings, demolition needs, and substrate/utility conflicts",
      "Permit, inspection, site-access, delivery, installation, and scheduling constraints"
    ],
    nearbyAreas: [
      "Downtown Reading",
      "Penn Square",
      "West Reading",
      "Wyomissing",
      "Muhlenberg Township",
      "Bern Township"
    ],
    references: [
      {
        context: "The city explains its planning, zoning, historic-preservation, building/trade permitting, property-maintenance inspections, and downtown/neighborhood planning responsibilities.",
        label: "City of Reading — Department of Community Development Overview",
        url: "https://www.readingpa.gov/department-of-community-development-overview"
      },
      {
        context: "The airport identifies itself as a general-aviation and full-service transportation center three miles northwest of Reading, with business, charter, training, museum, and public-safety-related facilities.",
        label: "Reading Regional Airport — RDG",
        url: "https://readingairport.org/"
      },
      {
        context: "The county’s MPO page lists current long-range transportation, improvement, active-transportation, corridor, freight, and system-condition planning resources serving the Reading region.",
        label: "County of Berks — Reading MPO Plans, Programs and Reports",
        url: "https://www.berkspa.gov/departments/planning-commission/transportation-reading-mpo/plans-and-programs"
      }
    ]
  },
  {
    stateSlug: "pennsylvania",
    citySlug: "erie",
    city: "Erie",
    stateName: "Pennsylvania",
    metaTitle: "Erie Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Erie projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Erie buyers may encounter restroom-partition needs in city parks and recreation sites, airport terminal facilities, commercial and institutional renovations, and neighborhood or mixed-use redevelopment across the city and county. City zoning guidance says interior renovations and use changes can require review, while the cited public sources describe planning and facility contexts only; they do not establish a Partition Stalls project, customer relationship, local office, local inventory, or automatic job acceptance, so any opportunity remains subject to project-specific review.",
    facilityTypes: [
      "City parks and recreation facilities",
      "Airport terminals and passenger facilities",
      "Commercial and mixed-use buildings",
      "Schools, colleges, and training facilities",
      "Healthcare and human-services facilities",
      "Municipal and community buildings"
    ],
    planningInputs: [
      "Facility address, municipality, and applicable zoning or permitting jurisdiction",
      "New construction, renovation, addition, or use-change scope",
      "Restroom count, compartment count, layout, and accessibility requirements",
      "Preferred partition material, finish, hardware, mounting conditions, and privacy configuration",
      "Site access, building operating hours, phasing, and coordination constraints",
      "Drawings, dimensions, specifications, and target procurement or installation milestones"
    ],
    nearbyAreas: [
      "Downtown Erie",
      "West Bayfront",
      "East Bayfront",
      "Millcreek Township",
      "Summit Township",
      "North East Borough",
      "Greene Township"
    ],
    references: [
      {
        context: "The city explains that most construction permits start with zoning review and that approval varies by project type, scope, and location, including interior renovations and use changes.",
        label: "City of Erie Zoning Department — Zoning Approvals & Permitting",
        url: "https://www.erie.pa.us/government/city_offices_departments/planning_and_neighborhood_resources/zoning.php"
      },
      {
        context: "The city’s adopted 20-year plan covers parks, recreation areas, open spaces, and public spaces, and identifies 52 city-owned parks and recreation sites plus two public golf courses.",
        label: "City of Erie — ERIE OUTSIDE! Comprehensive Recreation, Parks and Open Space Plan",
        url: "https://www.erie.pa.us/government/city_offices_departments/planning_and_neighborhood_resources/open_space.php"
      },
      {
        context: "The airport’s official page documents terminal amenities and spaces such as ticket counters, lobby areas, boarding areas, baggage claim, food service, and passenger-support facilities.",
        label: "Erie International Airport — Airport Amenities",
        url: "https://www.erieairport.org/travelers/airport-amenities"
      }
    ]
  },
  {
    stateSlug: "connecticut",
    citySlug: "bridgeport",
    city: "Bridgeport",
    stateName: "Connecticut",
    metaTitle: "Bridgeport Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Bridgeport projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Bridgeport buyers may encounter restroom-partition needs in civic and public buildings, downtown residential or mixed-use properties, neighborhood redevelopment settings, healthcare campuses, outpatient/urgent-care locations, and transit-connected commercial facilities. The cited sources describe Bridgeport’s planning and institutional context only; they do not establish a Partition Stalls project, customer relationship, local office, local inventory, or automatic ability to accept a job, so any opportunity requires project-specific review.",
    facilityTypes: [
      "Civic and public buildings",
      "Downtown residential and mixed-use properties",
      "Hospitals and regional medical centers",
      "Outpatient, urgent-care, and specialty medical facilities",
      "Higher-education and campus facilities",
      "Transit-connected commercial or public facilities"
    ],
    planningInputs: [
      "Facility address, building use, and applicable jurisdictional or owner requirements",
      "Number and layout of restroom compartments, including accessible and ambulatory requirements",
      "Panel material, color, hardware, mounting configuration, and privacy needs",
      "Existing-condition dimensions, substrate, plumbing clearances, and demolition or reuse scope",
      "Installation access, work hours, phasing, occupancy constraints, and site protection needs",
      "Drawings, specifications, finish schedule, bid deadline, and requested delivery/installation sequence"
    ],
    nearbyAreas: [
      "Downtown",
      "South End",
      "Black Rock",
      "East End",
      "The Hollow",
      "Fairfield",
      "Stratford"
    ],
    references: [
      {
        context: "The official city page describes Plan Bridgeport, development and planning functions, permitting-related services, downtown change, and neighborhood-focused public improvements.",
        label: "City of Bridgeport Office of Planning & Economic Development",
        url: "https://www.bridgeportct.gov/government/departments/planning-economic-development"
      },
      {
        context: "The official engagement page identifies 13 neighborhoods and 8 neighborhood revitalization zones and links plans for areas including Black Rock, East End, Hollow, and South End.",
        label: "Bridgeport Neighborhood Planning — Engage Bridgeport",
        url: "https://engage.bridgeportct.gov/neighborhoods"
      },
      {
        context: "The hospital’s official site identifies a regional hospital with Connecticut’s only dedicated Burn Center, emergency and urgent-care services, and multiple medical locations.",
        label: "Bridgeport Hospital",
        url: "https://www.bridgeporthospital.org/"
      }
    ]
  },
  {
    stateSlug: "connecticut",
    citySlug: "waterbury",
    city: "Waterbury",
    stateName: "Connecticut",
    metaTitle: "Waterbury Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Waterbury projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Waterbury buyers may encounter restroom-partition needs in municipal and downtown properties, higher-education and healthcare settings, transit-connected facilities, and nearby aviation, commercial, and industrial properties. Official sources describe these facility contexts and surrounding infrastructure only; they do not establish a Partition Stalls project, customer relationship, local office or inventory, or automatic ability to accept work, so any opportunity remains subject to project-specific review.",
    facilityTypes: [
      "Municipal and civic buildings",
      "Downtown higher-education facilities",
      "Healthcare and hospital facilities",
      "Transit and bus-service facilities",
      "Airport and aviation-service facilities",
      "Commercial and industrial properties"
    ],
    planningInputs: [
      "Facility address and operating context, including occupied, public-facing, or restricted areas",
      "Restroom count, room dimensions, ceiling heights, and an existing-condition plan or field measurements",
      "Partition layout, panel material/color, hardware, privacy requirements, and accessibility needs",
      "Installation phasing, shutdown or after-hours constraints, site access, loading, parking, and protection of occupied areas",
      "Permit, code, procurement, and building-owner requirements, with finish and schedule assumptions confirmed during project-specific review"
    ],
    nearbyAreas: [
      "Downtown Waterbury",
      "East End",
      "Bunker Hill",
      "Town Plot",
      "Naugatuck",
      "Watertown",
      "Middlebury"
    ],
    references: [
      {
        context: "The official city page identifies Waterbury City Hall and the municipal economic-development function, supporting civic and redevelopment-oriented facility context without documenting a Partition Stalls project.",
        label: "City of Waterbury — Economic Development",
        url: "https://www.waterburyct.org/services/economic-development"
      },
      {
        context: "The authority describes a public-use general-aviation airport near Waterbury with tenant facilities, parking, aviation services, and business-development surroundings.",
        label: "Connecticut Airport Authority — Waterbury-Oxford Airport",
        url: "https://ctairports.org/airports/waterbury-oxford/"
      },
      {
        context: "The official statewide transit site lists a Waterbury service division and routes, and explains local, express, and accessibility-oriented transit operations relevant to transportation facilities.",
        label: "CTtransit — official service and schedules site",
        url: "https://www.cttransit.com/"
      }
    ]
  },
  {
    stateSlug: "florida",
    citySlug: "jacksonville",
    city: "Jacksonville",
    stateName: "Florida",
    metaTitle: "Jacksonville Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Jacksonville projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Jacksonville buyers may encounter restroom-partition needs in airport terminals, transit hubs and stations, university campuses, downtown and commercial redevelopment, and industrial or logistics facilities. The City, JAX, and JTA sources describe these public and development contexts but do not establish a Partition Stalls project, local office or inventory, customer relationship, completed work, or automatic ability to accept a job; any opportunity remains subject to project-specific review.",
    facilityTypes: [
      "Airport terminals and related passenger facilities",
      "Transit hubs, stations, shelters, and intermodal facilities",
      "University and higher-education campuses",
      "Downtown, commercial, and mixed-use redevelopment",
      "Industrial, warehouse, and logistics facilities",
      "Municipal and other public-service facilities"
    ],
    planningInputs: [
      "Facility type, use, occupancy, and operating hours",
      "Architectural plans, restroom layouts, field dimensions, and mounting conditions",
      "Partition configuration, panel material, finish, hardware, and privacy requirements",
      "ADA/accessibility, plumbing, fire/life-safety, and applicable jurisdictional requirements",
      "Construction phasing, site access, protection of occupied areas, and coordination with other trades",
      "Desired procurement and installation window, subject to project-specific review rather than a fixed lead time"
    ],
    nearbyAreas: [
      "Downtown Jacksonville",
      "LaVilla",
      "Riverside/Avondale",
      "Southside",
      "Arlington",
      "Northside",
      "Orange Park"
    ],
    references: [
      {
        context: "The City describes targeted-industry recruitment, private capital investment, redevelopment of distressed areas, and downtown development under a master plan.",
        label: "City of Jacksonville Office of Economic Development",
        url: "https://www.coj.net/departments/office-of-economic-development.aspx"
      },
      {
        context: "JAX documents a 24-hour terminal with pre- and post-security amenities, passenger services, baggage-claim areas, concourses, and parking-related facilities.",
        label: "Jacksonville International Airport — Amenities & Services",
        url: "https://www.flyjacksonville.com/content.aspx?id=11"
      },
      {
        context: "JTA identifies accessible transit hubs, bus stops and shelters, seven Skyway stations, and the Jacksonville Regional Transportation Center at LaVilla.",
        label: "Jacksonville Transportation Authority — Transit Accessibility",
        url: "https://health.jtafla.com/legacy/about-jta/transit-accessibility/"
      }
    ]
  },
  {
    stateSlug: "florida",
    citySlug: "port-st-lucie",
    city: "Port St. Lucie",
    stateName: "Florida",
    metaTitle: "Port St. Lucie Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Port St. Lucie projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Port St. Lucie’s planning framework covers future land use, transportation, infrastructure, recreation and open space, economic development, capital improvements, and public school facilities, while city economic-development materials point to retail, office, industrial, and mixed-use growth contexts. Regional public facilities may also include transit/park-and-ride sites and aviation-related operations serving the wider county; any restroom-partition scope, code requirements, access conditions, and specifications require project-specific review. These public sources describe civic planning and regional facilities only and do not establish a Partition Stalls project, local office, inventory, completed project, customer relationship, or automatic ability to accept a job.",
    facilityTypes: [
      "Municipal offices and civic buildings",
      "Public schools and education facilities",
      "Parks, recreation centers, and community facilities",
      "Retail, office, and mixed-use developments",
      "Transit terminals and park-and-ride facilities",
      "Airport, aviation, and related commercial facilities"
    ],
    planningInputs: [
      "Facility type, address, and whether the work is new construction, renovation, or replacement",
      "Restroom count, partition layout, panel material/finish, hardware, and accessibility requirements",
      "Field dimensions, partition height, mounting conditions, door swings, and plumbing/fixture coordination",
      "Occupancy or anticipated user volume and peak-use considerations",
      "Site access, delivery/staging constraints, operating hours, and phasing requirements",
      "Applicable owner standards, permitting/code requirements, drawings, and requested installation scope"
    ],
    nearbyAreas: [
      "Tradition",
      "St. Lucie West",
      "Southern Grove",
      "Gatlin Boulevard / I-95 corridor",
      "River Park",
      "Fort Pierce",
      "Jensen Beach"
    ],
    references: [
      {
        context: "The official plan identifies future land use, transportation, infrastructure, recreation and open space, economic development, capital improvements, and public school facilities as planning elements and explains the city’s growth and zoning framework.",
        label: "City of Port St. Lucie — Comprehensive Plan",
        url: "https://www.cityofpsl.com/Government/Your-City-Government/Departments/Planning-Zoning/Comprehensive-Plan"
      },
      {
        context: "The city describes development-project approvals, zoning and building permitting, commercial and industrial sites, and development opportunities including Southern Grove, Village Green, East Lake Village, and Walton & One.",
        label: "City of Port St. Lucie — Economic Development",
        url: "https://www.cityofpsl.com/Business/Economic-Development"
      },
      {
        context: "The county describes a 3,844-acre general-aviation airport with aircraft maintenance, storage, manufacturing, flight training, charter, medical transport, customs, and rescue/firefighting operations.",
        label: "St. Lucie County — Treasure Coast International Airport",
        url: "https://www.stlucieco.gov/business/airport"
      }
    ]
  },
  {
    stateSlug: "texas",
    citySlug: "san-antonio",
    city: "San Antonio",
    stateName: "Texas",
    metaTitle: "San Antonio Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for San Antonio projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "San Antonio buyers may encounter restroom-partition needs in airport terminals, staffed transit centers, bioscience and corporate facilities, public or civic venues, and properties in historic districts where design review may apply. The cited public sources describe facility and market context only; they do not establish a Partition Stalls project, customer relationship, local office or inventory, completed work, or automatic job acceptance, so any opportunity requires project-specific review.",
    facilityTypes: [
      "Airport terminals and related passenger facilities",
      "Staffed transit centers and park-and-ride facilities",
      "Bioscience, healthcare, and research facilities",
      "Corporate offices and service facilities",
      "Civic, cultural, and public venues",
      "Historic-district commercial or institutional properties"
    ],
    planningInputs: [
      "Room plans, partition counts, dimensions, and layout",
      "Partition material, finish, color, hardware, and privacy requirements",
      "ADA and accessibility clearances, ambulatory compartments, and family or universal restroom needs",
      "Mounting conditions, substrate, plumbing locations, and field-measurement requirements",
      "Site access, occupied-facility constraints, delivery staging, and installation coordination",
      "Bid documents, approval requirements, phased-work needs, and target project milestones"
    ],
    nearbyAreas: [
      "Downtown San Antonio",
      "Medical Center",
      "River Walk and Mission Historic District areas",
      "Alamo Heights",
      "Leon Valley",
      "Schertz",
      "Universal City"
    ],
    references: [
      {
        context: "The city identifies San Antonio as part of the fast-growing Texas Triangle and highlights mobility, IT security and infrastructure, sustainable energy, bioscience, and corporate-services industry clusters.",
        label: "City of San Antonio Economic Development — About San Antonio",
        url: "https://www.sa.gov/Directory/Departments/EDD/About/San-Antonio"
      },
      {
        context: "The airport describes Terminal C, Terminal A and B upgrades, accessibility features, family and universal restrooms, and planned renovated restrooms in the terminal program.",
        label: "San Antonio International Airport — ELEVATE/SAT Terminal Development",
        url: "https://flysanantonio.com/business/about-saas/terminal-development/"
      },
      {
        context: "VIA states that its staffed transit centers serve as major transfer stations and provide enclosed waiting areas with restrooms, listing centers across San Antonio.",
        label: "VIA Metropolitan Transit — Transit Centers",
        url: "https://www.viainfo.net/transit-centers/"
      }
    ]
  },
  {
    stateSlug: "texas",
    citySlug: "dallas",
    city: "Dallas",
    stateName: "Texas",
    metaTitle: "Dallas Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Dallas projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Dallas facility planning may involve large convention and event venues, arenas and arts facilities, airport terminals and passenger services, public transportation stations, hotels, and civic or institutional buildings. The City of Dallas, DFW Airport, and DART sources describe public facilities and regional infrastructure only; they do not establish a Partition Stalls project, local office or inventory, completed project, customer relationship, automatic ability to accept a job, product guarantee, or fixed lead time, so any request requires project-specific review.",
    facilityTypes: [
      "Convention and exhibition centers",
      "Arenas and performing-arts venues",
      "Airport terminals and passenger-service areas",
      "Rail and transit stations",
      "Hotels and hospitality properties",
      "Civic, educational, and institutional buildings"
    ],
    planningInputs: [
      "Facility type, address, and whether the work is new construction, renovation, or phased replacement",
      "Partition layout, panel and door dimensions, compartment count, and accessibility clearances",
      "Material, finish, hardware, privacy, and mounting requirements",
      "Existing-condition photos or drawings, substrate details, and demolition or protection needs",
      "Site access, loading, staging, security, operating-hours, and coordination constraints",
      "Required bid documents, inspection requirements, and project-specific schedule milestones"
    ],
    nearbyAreas: [
      "Downtown Dallas",
      "The Cedars",
      "South Dallas",
      "South Oak Cliff",
      "West Oak Cliff",
      "DFW Airport area",
      "Richardson and Plano"
    ],
    references: [
      {
        context: "Official City Council material describes the convention-center expansion, ballroom, meeting rooms, exhibit halls, arena, arts, Union Station, transportation integration, and connections among Downtown, The Cedars, and South and Southern Dallas.",
        label: "City of Dallas — Kay Bailey Hutchison Convention Center Dallas Master Plan: Convention Center Component Update (May 21, 2025)",
        url: "https://dallascityhall.com/government/citymanager/Documents/Council%20Materials/Kay%20Bailey%20Hutchison%20Convention%20Center%20Dallas%20Master%20Plan%20Convention%20Center%20Component%20Update.pdf"
      },
      {
        context: "The airport’s official public page identifies terminal-based shops, dining, and passenger services, indicating an airport environment with multiple customer-facing facility areas.",
        label: "DFW International Airport — Shop/Dine/Services",
        url: "https://www.dfwairport.com/shop-dine-services"
      },
      {
        context: "DART’s official page describes a 93-mile, four-line system serving 66 stations in nine cities, including Downtown Dallas, South Dallas, Oak Cliff, North Dallas, and the DFW Airport connection.",
        label: "Dallas Area Rapid Transit — DART Rail System",
        url: "https://www.dart.org/guide/transit-and-use/rail"
      }
    ]
  },
  {
    stateSlug: "texas",
    citySlug: "fort-worth",
    city: "Fort Worth",
    stateName: "Texas",
    metaTitle: "Fort Worth Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Fort Worth projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Fort Worth-area buyers may encounter restroom partition needs in civic community centers, downtown and mixed-use urban-village projects, cultural and event venues, transit stations, and large airport-related facilities serving the region. The cited public sources describe these facility and district contexts only; they do not establish a Partition Stalls project, customer relationship, local office or inventory, automatic job acceptance, product guarantee, or fixed lead time, so any opportunity remains subject to project-specific review.",
    facilityTypes: [
      "Community centers and recreation facilities",
      "Civic and municipal buildings",
      "Downtown, urban-village, and mixed-use developments",
      "Cultural, convention, and event venues",
      "Transit stations and transfer facilities",
      "Airport terminals and related aviation facilities"
    ],
    planningInputs: [
      "Facility address, district, and access or parking constraints",
      "New construction, renovation, or replacement scope",
      "Partition layout, panel dimensions, material, hardware, and finish requirements",
      "Restroom count, fixture layout, accessibility clearances, and applicable code requirements",
      "Site phasing, operating-hours restrictions, delivery access, and installation coordination",
      "Owner, architect, general contractor, and approval or submittal requirements"
    ],
    nearbyAreas: [
      "Downtown Fort Worth",
      "Cultural District",
      "Historic Stockyards",
      "Near Southside/Historic Southside",
      "Northside",
      "North Richland Hills",
      "Grapevine"
    ],
    references: [
      {
        context: "The City identifies urban villages, public improvement districts, redevelopment initiatives, TIF infrastructure, and the Evans & Rosedale district as active economic-development contexts.",
        label: "City of Fort Worth Economic Development",
        url: "https://www.fortworthtexas.gov/departments/econdev"
      },
      {
        context: "Trinity Metro lists Fort Worth T&P, Fort Worth Central, North Side, Mercantile Center, and regional TEXRail/TRE stations connecting Fort Worth with North Richland Hills, Grapevine, and DFW Airport.",
        label: "Trinity Metro Train Stations",
        url: "https://ridetrinitymetro.org/stations/"
      },
      {
        context: "DFW Airport reports five terminals, more than 160 gates, modernized facilities, and its location centered between Dallas and Fort Worth, establishing a major regional aviation-facility context.",
        label: "DFW Airport About DFW & Fast Facts",
        url: "https://www.dfwairport.com/business/about/facts/"
      }
    ]
  },
  {
    stateSlug: "california",
    citySlug: "san-jose",
    city: "San Jose",
    stateName: "California",
    metaTitle: "San Jose Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for San Jose projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "San José buyers may encounter restroom-partition needs in city-regulated development and renovation, airport terminals with restrooms in multiple public areas, university buildings and campus housing, and transit-oriented mixed-use projects near major stations. These sources describe local planning and facility contexts only; they do not establish a Partition Stalls project, local office or inventory, completed work, customer relationship, automatic job acceptance, product guarantee, or fixed lead time, so any opportunity requires project-specific review.",
    facilityTypes: [
      "Municipal and civic buildings",
      "Airport terminals and aviation facilities",
      "University academic, athletic, and housing facilities",
      "Transit stations and transit-oriented mixed-use developments",
      "Office, retail, and employment buildings",
      "Hotels and multifamily residential properties"
    ],
    planningInputs: [
      "Project address, jurisdiction, and applicable permitting or zoning path",
      "New construction, renovation, replacement, or phased-work scope",
      "Restroom count, layout, stall count, and required panel dimensions",
      "Partition material, finish, hardware, privacy, accessibility, and code requirements",
      "Site access, phasing, protection, demolition, and installation constraints",
      "Drawings, specifications, bid documents, and target procurement milestones"
    ],
    nearbyAreas: [
      "Downtown San José",
      "North San José",
      "San José Mineta International Airport area",
      "Santa Clara",
      "Milpitas",
      "Campbell",
      "East San José"
    ],
    references: [
      {
        context: "The City says its Planning Division oversees General Plan and zoning policies, reviews planning-permit applications, and engages communities on proposed developments.",
        label: "City of San José Planning Division",
        url: "https://www.sanjoseca.gov/your-government/departments-offices/planning-building-code-enforcement/planning-division"
      },
      {
        context: "The official airport page identifies terminals A and B, public services, and restrooms located at ticketing, baggage-claim, and gate areas.",
        label: "San José Mineta International Airport Services & Amenities",
        url: "https://www.flysanjose.com/Services-Amenities"
      },
      {
        context: "VTA describes a nearly four-acre downtown site beside the future BART station with potential mixed-use housing, retail, employment, public-realm, access, and infrastructure coordination.",
        label: "VTA Downtown San José Station Transit-Oriented Development",
        url: "https://www.vta.org/projects/downtown-san-jose-station-transit-oriented-development"
      }
    ]
  },
  {
    stateSlug: "california",
    citySlug: "san-francisco",
    city: "San Francisco",
    stateName: "California",
    metaTitle: "San Francisco Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for San Francisco projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "San Francisco buyers may encounter restroom partition needs in downtown offices, retail and hotel properties, cultural or institutional buildings, transit facilities, and airport-related passenger amenities. A project-specific review is appropriate for existing conditions, access, and scope; these public sources describe facility contexts only and do not establish a Partition Stalls project, local office or inventory, completed work, customer relationship, automatic job acceptance, product guarantee, or fixed lead time.",
    facilityTypes: [
      "Office and mixed-use commercial buildings",
      "Retail, restaurant, and hospitality properties",
      "Hotels and visitor facilities",
      "Cultural and institutional buildings",
      "Muni transit facilities and stations",
      "Airport terminals and passenger-service areas"
    ],
    planningInputs: [
      "Facility type, address, and district or municipality",
      "Existing partition panel material, layout, dimensions, and mounting conditions",
      "Number of restroom compartments, openings, and accessibility requirements",
      "Occupancy, operating hours, phasing, and site access constraints",
      "Finish, hardware, privacy, and vandal-resistance preferences",
      "Demolition, disposal, permitting, and installation coordination responsibilities"
    ],
    nearbyAreas: [
      "Downtown / Financial District",
      "Union Square",
      "Chinatown",
      "SoMa / South of Market",
      "Mission Bay",
      "Van Ness Corridor",
      "South San Francisco / SFO area"
    ],
    references: [
      {
        context: "The City’s plan describes downtown office, retail, hotel, housing, cultural, institutional, industrial, and support-commercial uses, including clusters around the Financial District, Union Square, and South of Market.",
        label: "San Francisco General Plan — Downtown Area Plan",
        url: "https://generalplan.sfplanning.org/Downtown.htm"
      },
      {
        context: "SFO’s official passenger page identifies terminal amenities and services such as lounges, showers, a medical clinic, nursing rooms, and an on-airport hotel.",
        label: "San Francisco International Airport — Services & Amenities",
        url: "https://www.flysfo.com/passengers/services-amenities"
      },
      {
        context: "SFMTA states that Muni’s citywide network includes buses, Metro light rail, historic streetcars, and cable cars, with routes, stops, and improvement projects.",
        label: "SFMTA — Muni",
        url: "https://www.sfmta.com/muni-transit"
      }
    ]
  },
  {
    stateSlug: "california",
    citySlug: "fresno",
    city: "Fresno",
    stateName: "California",
    metaTitle: "Fresno Bathroom Partitions | Partition Stalls",
    metaDescription: "Commercial bathroom partitions for Fresno projects. Send plans, site details, access conditions, and schedule needs for a focused quote.",
    marketContext: "Fresno buyers may encounter restroom-partition needs in commercial and residential development subject to City planning, building-safety, zoning, and permit review, as well as in transportation settings such as Fresno Area Express facilities and Fresno Yosemite International Airport terminal, cargo, parking, and support facilities. The cited public sources describe planning and facility contexts only; they do not establish a Partition Stalls project, local office or inventory, completed project, customer relationship, automatic job acceptance, product guarantee, or fixed lead time, so any supply-and-installation scope requires project-specific review.",
    facilityTypes: [
      "Commercial offices, retail, and hospitality properties",
      "Multifamily and residential developments",
      "Airport passenger-terminal and landside facilities",
      "Transit and bus-support facilities",
      "Public or institutional buildings",
      "Cargo, aviation, and general-aviation facilities"
    ],
    planningInputs: [
      "Facility use, occupancy, and applicable permit or plan-review status",
      "Restroom layout, compartment count, dimensions, and accessibility clearances",
      "Panel material, finish, height, hardware, and privacy requirements",
      "Existing substrate, attachment conditions, plumbing conflicts, and field measurements",
      "Site access, work-hour restrictions, phasing, and protection of occupied areas",
      "Requested drawings, specifications, budget range, and target procurement window"
    ],
    nearbyAreas: [
      "Downtown Fresno",
      "Tower District",
      "Fig Garden",
      "West Fresno",
      "Sunnyside",
      "Clovis",
      "Sanger"
    ],
    references: [
      {
        context: "The City describes its role in regulating and assisting residential and commercial development, including planning, building-safety, zoning, permits, and development review.",
        label: "City of Fresno Planning and Development",
        url: "https://www.fresno.gov/planning/"
      },
      {
        context: "The official transit page identifies Fresno Area Express rider services, routes, shelters/stops, accessibility, and related facilities information.",
        label: "City of Fresno FAX: Fresno Area Express",
        url: "https://www.fresno.gov/transportation/fax/"
      },
      {
        context: "The City of Fresno Airports Department master-plan summary discusses the airport as a regional transportation hub and identifies terminal, parking, cargo/general-aviation, and airport-support facility needs.",
        label: "Fresno Yosemite International Airport Master Plan Update 2018",
        url: "https://flyfresno.com/wp-content/uploads/2019/05/FAT-Master-Plan-ExecSum.pdf"
      }
    ]
  },
] as const satisfies readonly CityServiceAreaContent[];
